import { OrderStatus as OrderStatusEnum, orderStatusMapping } from "@enums";
import { Person as PersonIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import PageHeader from "@pages/Layout/PageHeader";
import { OrderTrackerDrawer } from "@pages/OrderTracker/components";
import DrawerControlButtons from "@pages/OrderTracker/components/DrawerControlButtons";
import useOrderTrackerDrawer from "@pages/OrderTracker/components/OrderTrackerDrawer/useOrderTrackerDrawer";
import ApprovePlanModal from "@pages/PhysicianReview/components/ApprovePlanModal";
import LeftSideBar from "@pages/PhysicianReview/components/LeftSideBar";
import RejectPlanModal from "@pages/PhysicianReview/components/RejectPlanModal";
import {
  OrderStatus,
  useAutomationIntegrationApiV1GetPlanGenerationQuery,
  useWebApiV1ListAttachmentsQuery,
  useWebApiV1ListSiteGroupsQuery,
  useWebApiV1OrderQuery,
} from "@providers/hop-ord-server/api";
import { skipToken } from "@reduxjs/toolkit/query";
import { toLocaleDateTimeString } from "@utils";
import { getCompletedPlanTimestamps } from "@volatile/pages/OrderTracker/components";
import {
  ComparisonView,
  SinglePlanView,
} from "@volatile/pages/OrderTracker/pages";
import { useState } from "react";
import { useParams } from "react-router-dom";

const scrollToId = (id: string) => {
  const element = document.getElementById(id);
  element?.scrollIntoView({ behavior: "smooth" });
};

const statusLabel = (orderStatus: OrderStatus) => {
  switch (orderStatus) {
    case "ready_for_ro_approval":
      return "Pending";
    case "approved":
      return "Approved";
    case "rejected":
      return "Rejected";
    default:
      return orderStatusMapping[orderStatus];
  }
};

const statusColor = (orderStatus: OrderStatus) => {
  switch (orderStatus) {
    case "ready_for_ro_approval":
      return "warning";
    case "approved":
      return "success";
    case "rejected":
      return "error";
    default:
      return "warning";
  }
};

// Centralized plan configuration to eliminate duplication
const PLAN_CONFIG = {
  planA: { label: "Prostate Nodes R1", shortId: "planA" },
  planB: { label: "Prostate Nodes R2", shortId: "planB" },
} as const;

// Plan selection types and utilities
export interface PlanOption {
  value: string;
  label: string;
}

export const planOptions: PlanOption[] = Object.entries(PLAN_CONFIG).map(
  ([value, config]) => ({
    value,
    label: config.label,
  }),
);

export const usePlanSelection = () => {
  // Single plan mode state
  const [selectedPlanOption, setSelectedPlanOption] = useState("planA");

  // Comparison mode state
  const [selectedPlan, setSelectedPlan] = useState("planA");
  const [selectedPlanAOption, setSelectedPlanAOption] = useState("planA");
  const [selectedPlanBOption, setSelectedPlanBOption] = useState("planB");

  const getPlanDisplayName = (plan: string) => {
    // First check if it's a full plan key
    if (plan in PLAN_CONFIG) {
      return PLAN_CONFIG[plan as keyof typeof PLAN_CONFIG].label;
    }

    // Then check if it's a short ID
    const planEntry = Object.entries(PLAN_CONFIG).find(
      ([_, config]) => config.shortId === plan,
    );

    if (planEntry) {
      return planEntry[1].label;
    }

    // Default fallback
    return "Prostate VMAT";
  };

  // Filter available options based on current selections in comparison mode
  const getAvailablePlanAOptions = () => {
    return planOptions.filter((option) => option.value !== selectedPlanBOption);
  };

  const getAvailablePlanBOptions = () => {
    return planOptions.filter((option) => option.value !== selectedPlanAOption);
  };

  return {
    // Single mode
    selectedPlanOption,
    setSelectedPlanOption,

    // Comparison mode
    selectedPlan,
    setSelectedPlan,
    selectedPlanAOption,
    setSelectedPlanAOption,
    selectedPlanBOption,
    setSelectedPlanBOption,

    // Shared utilities
    planOptions,
    getPlanDisplayName,
    getAvailablePlanAOptions,
    getAvailablePlanBOptions,
  };
};

// Format DOB to US format with 3-letter month and dashes
const formatDobToUS = (dob: string | null) => {
  if (!dob) return "N/A";

  try {
    const date = new Date(dob);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
    };
    return date
      .toLocaleDateString("en-US", options)
      .replace(/,\s/g, "-")
      .replace(/\s/g, "-");
  } catch {
    return dob; // Return original if parsing fails
  }
};

// Send for Review Confirmation Modal Component (updated to show "Submitted")
const SendForReviewModal = ({
  open,
  onClose,
  onConfirm,
  selectedPlan,
  getPlanDisplayName,
  patientName,
  patientMrn,
  patientDob,
  planGenerationData: _planGenerationData,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedPlan: string;
  getPlanDisplayName: (plan: string) => string;
  patientName: string | null;
  patientMrn: string | null;
  patientDob: string | null;
  planGenerationData: unknown[] | undefined;
}) => {
  // Function to get the timestamp for the selected plan
  const getPlanTimestamp = (planKey: string) => {
    // Always use localStorage timestamps to match CompletedPlans table (ignore planGenerationData)
    const timestamps = getCompletedPlanTimestamps();

    // Explicit mapping to ensure consistency with CompletedPlans table
    // planA → "Prostate Nodes R1" → plan1FinishedAt
    // planB → "Prostate Nodes R2" → plan2FinishedAt
    if (planKey === "planA") {
      return toLocaleDateTimeString(timestamps.plan1FinishedAt);
    } else if (planKey === "planB") {
      return toLocaleDateTimeString(timestamps.plan2FinishedAt);
    }

    // Fallback by plan display name
    const planDisplayName = getPlanDisplayName(planKey);
    if (planDisplayName === "Prostate Nodes R1") {
      return toLocaleDateTimeString(timestamps.plan1FinishedAt);
    } else if (planDisplayName === "Prostate Nodes R2") {
      return toLocaleDateTimeString(timestamps.plan2FinishedAt);
    }

    return "Mar-17-2024, 3:44 PM"; // Final fallback
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 1,
        },
      }}
    >
      <DialogTitle sx={{ paddingBottom: 1 }}>
        <Typography variant="h6">Send for review?</Typography>
      </DialogTitle>

      <DialogContent sx={{ paddingTop: 1, paddingBottom: 2 }}>
        <Stack gap={3}>
          <Typography variant="body1">
            Are you sure you want to send the following plan for Physician
            approval?
          </Typography>

          <Stack gap={1}>
            <Typography variant="body1">
              {getPlanDisplayName(selectedPlan)}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Created: {getPlanTimestamp(selectedPlan)}
            </Typography>
          </Stack>

          <Divider />

          <Stack direction="row" alignItems="center" gap={2}>
            <Stack
              width={24}
              height={24}
              borderRadius={1}
              bgcolor="primary.main"
              alignItems="center"
              justifyContent="center"
            >
              <PersonIcon sx={{ color: "white", fontSize: 16 }} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                {patientName || "Unknown Patient"}
              </Typography>
              <Typography variant="body2" sx={{ mx: 2 }}>
                Patient ID: {patientMrn || "N/A"}
              </Typography>
              <Typography variant="body2">
                DOB: {formatDobToUS(patientDob)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ padding: 2, paddingTop: 0 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ textTransform: "uppercase" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          sx={{ textTransform: "uppercase" }}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PhysicianReviewPage = () => {
  const { orderId } = useParams();
  const [sendForReviewModalOpen, setSendForReviewModalOpen] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [approvePlanModalOpen, setApprovePlanModalOpen] = useState(false);
  const [rejectPlanModalOpen, setRejectPlanModalOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const planSelection = usePlanSelection();

  const { data: orderData } = useWebApiV1OrderQuery({
    orderId: Number(orderId),
  });

  const { data: siteGroupData } = useWebApiV1ListSiteGroupsQuery({
    orderId: Number(orderId),
  });

  const { isLoading: scorecardAttachmentsLoading } =
    useWebApiV1ListAttachmentsQuery(
      orderId !== undefined
        ? {
            orderId: Number(orderId),
          }
        : skipToken,
    );

  // Fetch plan generation data to get the correct timestamps
  const { data: planGenerationData } =
    useAutomationIntegrationApiV1GetPlanGenerationQuery({
      orderId: Number(orderId),
    });

  const {
    drawerOpen,
    setDrawerOpen,
    selectedDrawerPanel,
    setSelectedDrawerPanel,
  } = useOrderTrackerDrawer();

  const status = orderData?.status;

  const handleConfirmSendForReview = () => {
    setSendForReviewModalOpen(false);
    setShowSuccessAlert(true);
  };

  const handleCloseAlert = () => {
    setShowSuccessAlert(false);
  };

  const handleApprove = () => {
    setApprovePlanModalOpen(false);
    setToastMessage(
      "You've approved the plan. The planning team has been notified.",
    );
    setToastOpen(true);
    scrollToId("plan-review-title");
  };

  const handleReject = (success: boolean) => {
    setRejectPlanModalOpen(false);
    const message = success
      ? "Plan rejected. Your reason has been shared with the planning team."
      : "Error rejecting plan. Please try again or contact support.";
    setToastMessage(message);
    setToastOpen(true);
    scrollToId("plan-review-title");
  };

  const handleCompareToggle = () => {
    setIsComparisonMode(!isComparisonMode);
  };

  // Get the currently selected plan based on the mode
  const currentlySelectedPlan = isComparisonMode
    ? planSelection.selectedPlan === "planA"
      ? planSelection.selectedPlanAOption
      : planSelection.selectedPlanBOption
    : planSelection.selectedPlanOption;

  return (
    <>
      <Stack direction="row" height={1}>
        <LeftSideBar />
        <Stack flexGrow={1}>
          <PageHeader orderData={orderData} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              paddingTop: 3,
              paddingBottom: isComparisonMode ? 0 : 2,
              paddingLeft: 8,
              paddingRight: 8,
              overflowY: "scroll",
              flexGrow: 1,
            }}
          >
            <Stack
              sx={{
                maxWidth: "1500px",
                width: "auto",
                gap: 2,
                height: "fit-content",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" gap={1}>
                  <Typography id="plan-review-title" variant="h5">
                    Plan Review
                  </Typography>
                  {status && (
                    <Chip
                      label={statusLabel(status)}
                      data-testid="status-chip-label"
                      color={statusColor(status)}
                      sx={{
                        width: "fit-content",
                        color: "text.primary",
                      }}
                      variant="filled"
                    />
                  )}
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isComparisonMode}
                      onChange={handleCompareToggle}
                      color="primary"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#745de5",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#745de5",
                          },
                      }}
                    />
                  }
                  label="Compare"
                  labelPlacement="start"
                />
              </Stack>
              <DrawerControlButtons
                setDrawerOpen={setDrawerOpen}
                setSelectedDrawerPanel={setSelectedDrawerPanel}
              />

              {/* Route to appropriate view based on mode */}
              {isComparisonMode ? (
                <ComparisonView
                  orderId={Number(orderId)}
                  scorecardAttachmentsLoading={scorecardAttachmentsLoading}
                  stickyTopOffset={-25}
                  dateLabel="Created"
                  planSelection={planSelection}
                  planGenerationData={planGenerationData}
                  isPhysicianReview={true}
                />
              ) : (
                <SinglePlanView
                  orderId={Number(orderId)}
                  orderData={orderData}
                  scorecardAttachmentsLoading={scorecardAttachmentsLoading}
                  dateLabel="Created"
                  planSelection={planSelection}
                  planGenerationData={planGenerationData}
                />
              )}
            </Stack>
          </Box>

          {/* Reject/Approve buttons footer */}
          {orderData?.status === OrderStatusEnum.READY_FOR_RO_APPROVAL && (
            <Stack
              direction="row"
              gap={2}
              sx={{
                justifyContent: "flex-end",
                padding: 2,
                borderTop: `1px solid`,
                borderColor: "divider",
              }}
            >
              <Button
                variant="contained"
                color="error"
                onClick={() => setRejectPlanModalOpen(true)}
                size="large"
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                size="large"
                onClick={() => setApprovePlanModalOpen(true)}
              >
                Approve
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>

      {/* Modals */}
      <ApprovePlanModal
        orderId={Number(orderId)}
        open={approvePlanModalOpen}
        onApprove={handleApprove}
        onClose={() => setApprovePlanModalOpen(false)}
      />
      <RejectPlanModal
        open={rejectPlanModalOpen}
        setOpen={setRejectPlanModalOpen}
        order={orderData}
        onReject={handleReject}
      />
      <OrderTrackerDrawer
        orderId={Number(orderId)}
        open={drawerOpen}
        setOpen={setDrawerOpen}
        selectedPanel={selectedDrawerPanel}
        setSelectedPanel={setSelectedDrawerPanel}
        orderData={orderData}
        siteGroupData={siteGroupData || []}
      />

      <SendForReviewModal
        open={sendForReviewModalOpen}
        onClose={() => setSendForReviewModalOpen(false)}
        onConfirm={handleConfirmSendForReview}
        selectedPlan={currentlySelectedPlan}
        getPlanDisplayName={planSelection.getPlanDisplayName}
        patientName={orderData?.patientName ?? null}
        patientMrn={orderData?.patientMrn ?? null}
        patientDob={orderData?.patientDob ?? null}
        planGenerationData={planGenerationData}
      />

      {/* Toast notifications */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={4000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity="success"
          sx={{ width: "100%" }}
        >
          Plan submitted for review
        </Alert>
      </Snackbar>
    </>
  );
};

export default PhysicianReviewPage;
